if (!window.Eurus.loadedScript.has('almapharm-wishlist.js')) {
  window.Eurus.loadedScript.add('almapharm-wishlist.js');

  const WISHLIST_BUTTON_SELECTOR =
    '.mc-card__wishlist, .pdp-wishlist-button, .cart-wishlist-button, [data-pct-wishlist]';
  const WISHLIST_READY_EVENTS = [
    'wk:app:ready',
    'wk:loader:wishlist-app:load',
  ];
  const WISHLIST_SYNC_EVENTS = [
    ...WISHLIST_READY_EVENTS,
    'wk:wishlist:add:success',
    'wk:wishlist:remove:success',
    'wk:wishlist:clear:success',
  ];

  const activeProductIds = new Set();
  const wishlistItemIdByProductId = new Map();
  /** @type {Map<string, boolean>} productId -> desired in-wishlist while API is in flight */
  const pendingByProductId = new Map();
  let wishlistKingPromise = null;
  let wishlistObserveSubscription = null;

  function parseVariantId(variantId) {
    const numeric = Number(variantId);
    return Number.isFinite(numeric) ? numeric : variantId;
  }

  function normalizeProductId(productId) {
    if (productId == null || productId === '') return '';
    const value = String(productId);
    const gidMatch = value.match(/Product\/(\d+)$/);
    return gidMatch ? gidMatch[1] : value;
  }

  function isWishlistKingReady() {
    return typeof window.WishlistKing?.addWishlistItem === 'function';
  }

  function waitForWishlistKing(timeoutMs = 60000) {
    if (isWishlistKingReady()) {
      return Promise.resolve(window.WishlistKing);
    }

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        window.clearInterval(pollId);
        reject(new Error('Wishlist King did not initialize in time'));
      }, timeoutMs);

      const finish = () => {
        if (!isWishlistKingReady()) return;
        window.clearTimeout(timeoutId);
        window.clearInterval(pollId);
        resolve(window.WishlistKing);
      };

      const pollId = window.setInterval(finish, 50);

      WISHLIST_READY_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, finish, { once: true });
      });
    });
  }

  function getWishlistKing() {
    if (!wishlistKingPromise) {
      wishlistKingPromise = waitForWishlistKing().catch((error) => {
        wishlistKingPromise = null;
        console.warn('[almapharm-wishlist] Wishlist King failed to load', error);
        throw error;
      });
    }
    return wishlistKingPromise;
  }

  function applyButtonState(button, isActive) {
    button.classList.toggle('is-in-wishlist', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  function updateButtonStates() {
    document.querySelectorAll(WISHLIST_BUTTON_SELECTOR).forEach((button) => {
      const productId = normalizeProductId(button.dataset.productId);
      if (!productId) return;
      applyButtonState(button, activeProductIds.has(productId));
    });
  }

  function updateCartButtonStates() {
    document.querySelectorAll('#CartDrawer .cart-wishlist-button').forEach((button) => {
      const productId = normalizeProductId(button.dataset.productId);
      if (!productId) return;
      applyButtonState(button, activeProductIds.has(productId));
    });
  }

  function updateWishlistCountBubbles(count) {
    document.querySelectorAll('.js-wishlist-count-bubble').forEach((bubble) => {
      bubble.dataset.value = String(count);
      bubble.textContent = count < 100 ? String(count) : '99+';
      bubble.classList.toggle('hidden', count === 0);
    });
  }

  function setProductWishlistState(productId, inWishlist, wishlistItemId) {
    const key = normalizeProductId(productId);
    if (inWishlist) {
      activeProductIds.add(key);
      if (wishlistItemId != null) {
        wishlistItemIdByProductId.set(key, wishlistItemId);
      }
    } else {
      activeProductIds.delete(key);
      wishlistItemIdByProductId.delete(key);
    }
    updateButtonStates();
  }

  function applyPendingOverrides() {
    pendingByProductId.forEach((inWishlist, productId) => {
      if (inWishlist) {
        activeProductIds.add(productId);
      } else {
        activeProductIds.delete(productId);
      }
    });
  }

  function clearSettledPending() {
    pendingByProductId.forEach((inWishlist, productId) => {
      if (activeProductIds.has(productId) === inWishlist) {
        pendingByProductId.delete(productId);
      }
    });
  }

  function syncButtonsFromWishlist(wishlist) {
    activeProductIds.clear();
    wishlistItemIdByProductId.clear();

    (wishlist?.items || []).forEach((item) => {
      const productIdKey = normalizeProductId(item?.product?.id);
      if (!productIdKey) return;

      activeProductIds.add(productIdKey);
      if (item.id != null) {
        wishlistItemIdByProductId.set(productIdKey, item.id);
      }
    });

    applyPendingOverrides();
    updateButtonStates();
    updateWishlistCountBubbles((wishlist?.items || []).length);
    clearSettledPending();
  }

  function observeWishlist(app) {
    if (wishlistObserveSubscription || !app?.state?.observeWishlist) return;

    wishlistObserveSubscription = app.state
      .observeWishlist({ wishlistId: 'mine' })
      .subscribe(syncButtonsFromWishlist);
  }

  async function fetchCurrentWishlist(app) {
    const fetchers = [
      () => app.state?.getWishlist?.({ wishlistId: 'mine' }),
      () => app.state?.loadWishlist?.({ wishlistId: 'mine' }),
      () => app.getWishlist?.({ wishlistId: 'mine' }),
    ];

    for (const fetchWishlist of fetchers) {
      if (typeof fetchWishlist !== 'function') continue;
      try {
        const wishlist = await fetchWishlist();
        if (wishlist?.items) return wishlist;
      } catch (_) {}
    }

    return null;
  }

  async function loadInitialWishlist() {
    const app = await getWishlistKing();
    observeWishlist(app);

    const wishlist = await fetchCurrentWishlist(app);
    if (wishlist) {
      syncButtonsFromWishlist(wishlist);
      return;
    }

    updateButtonStates();
  }

  async function persistWishlistToggle(button, wasInWishlist, previousItemId) {
    const app = await getWishlistKing();
    const productHandle = button.dataset.productHandle;
    const variantId = parseVariantId(button.dataset.variantId);
    const productId = normalizeProductId(button.dataset.productId);

    if (!productHandle) {
      throw new Error('Product handle is required for Wishlist King');
    }

    if (wasInWishlist) {
      const wishlistItemId = previousItemId ?? wishlistItemIdByProductId.get(productId);
      if (!wishlistItemId) {
        throw new Error('Wishlist item id not found');
      }
      await app.removeWishlistItem({ wishlistItemId });
    } else {
      await app.addWishlistItem({ productHandle, variantId });
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(WISHLIST_BUTTON_SELECTOR);
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const productId = normalizeProductId(button.dataset.productId);
    if (!productId) return;

    const wasInWishlist = activeProductIds.has(productId);
    const nextInWishlist = !wasInWishlist;
    const previousItemId = wishlistItemIdByProductId.get(productId);

    pendingByProductId.set(productId, nextInWishlist);
    setProductWishlistState(productId, nextInWishlist);
    button.setAttribute('aria-busy', 'true');

    persistWishlistToggle(button, wasInWishlist, previousItemId)
      .catch((error) => {
        pendingByProductId.delete(productId);
        setProductWishlistState(productId, wasInWishlist, previousItemId);
        console.warn('[almapharm-wishlist] toggle failed', error);
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        clearSettledPending();
      });
  });

  WISHLIST_SYNC_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, () => {
      loadInitialWishlist().catch(() => {});
    });
  });

  let cartWishlistSyncFrame = null;

  function afterCartDomUpdate() {
    updateButtonStates();
    updateCartButtonStates();
    requestAnimationFrame(() => {
      updateButtonStates();
      updateCartButtonStates();
    });
  }

  function afterCartDomUpdateSoon() {
    afterCartDomUpdate();
    window.setTimeout(afterCartDomUpdate, 50);
    window.setTimeout(afterCartDomUpdate, 200);
  }

  function scheduleCartWishlistSync() {
    if (cartWishlistSyncFrame != null) {
      cancelAnimationFrame(cartWishlistSyncFrame);
    }
    cartWishlistSyncFrame = requestAnimationFrame(() => {
      cartWishlistSyncFrame = null;
      afterCartDomUpdate();
    });
  }

  document.addEventListener('eurus:cart:items-changed', () => {
    observeCartDrawer();
    scheduleCartWishlistSync();
  });

  function observeCartDrawer() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer || cartDrawer.dataset.wishlistObserve) return;

    cartDrawer.dataset.wishlistObserve = 'true';
    new MutationObserver((mutations) => {
      if (
        mutations.some(
          (mutation) => mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0
        )
      ) {
        scheduleCartWishlistSync();
      }
    }).observe(cartDrawer, { childList: true, subtree: true });
  }

  function waitForMiniCartReload(store, onComplete) {
    const wait = () => {
      if (store.loading) {
        requestAnimationFrame(wait);
        return;
      }
      requestAnimationFrame(() => {
        window.setTimeout(onComplete, 0);
      });
    };
    requestAnimationFrame(wait);
  }

  function watchMiniCartLoading() {
    if (window.__almapharmWishlistLoadingWatch) return;
    window.__almapharmWishlistLoadingWatch = true;

    let wasLoading = false;
    const poll = () => {
      const store = window.Alpine?.store?.('xMiniCart');
      if (store) {
        if (wasLoading && !store.loading) {
          afterCartDomUpdateSoon();
        }
        wasLoading = store.loading;
      }
      requestAnimationFrame(poll);
    };
    poll();
  }

  function patchMiniCartReload() {
    const alpine = window.Alpine;
    if (!alpine?.store) return false;

    const store = alpine.store('xMiniCart');
    if (!store) return false;

    if (!store.__almapharmWishlistReloadPatched) {
      store.__almapharmWishlistReloadPatched = true;
      const originalReLoad = store.reLoad.bind(store);

      store.reLoad = function patchedReLoad() {
        originalReLoad();
        waitForMiniCartReload(store, afterCartDomUpdate);
      };
    }

    if (!store.__almapharmWishlistOpenPatched) {
      store.__almapharmWishlistOpenPatched = true;
      const originalOpenCart = store.openCart.bind(store);

      store.openCart = function patchedOpenCart() {
        originalOpenCart();
        afterCartDomUpdateSoon();
      };
    }

    return true;
  }

  function patchCartHelperRerender() {
    const alpine = window.Alpine;
    if (!alpine?.store) return false;

    const helper = alpine.store('xCartHelper');
    if (!helper || helper.__almapharmWishlistPatched) return true;

    helper.__almapharmWishlistPatched = true;
    const originalReRenderSections = helper.reRenderSections.bind(helper);

    helper.reRenderSections = async function patchedReRenderSections(...args) {
      await originalReRenderSections(...args);
      afterCartDomUpdate();
    };

    return true;
  }

  function ensureCartPatches() {
    const miniCartPatched = patchMiniCartReload();
    const helperPatched = patchCartHelperRerender();
    return miniCartPatched && helperPatched;
  }

  function waitForCartPatches(attempt = 0) {
    if (ensureCartPatches() || attempt > 240) return;
    requestAnimationFrame(() => waitForCartPatches(attempt + 1));
  }

  function schedulePageWishlistResync() {
    loadInitialWishlist()
      .then(afterCartDomUpdate)
      .catch(afterCartDomUpdate);

    window.setTimeout(afterCartDomUpdate, 150);
    window.setTimeout(afterCartDomUpdate, 600);
    window.setTimeout(afterCartDomUpdate, 1200);
  }

  function handlePageShow(event) {
    wishlistObserveSubscription = null;
    observeCartDrawer();
    waitForCartPatches();
    watchMiniCartLoading();

    const syncWhenAlpineReady = () => {
      ensureCartPatches();
      const store = window.Alpine?.store?.('xMiniCart');
      if (event.persisted && store?.needReload && !store.loading) {
        store.reLoad();
      }
      schedulePageWishlistResync();
    };

    if (window.Alpine?.store?.('xMiniCart')) {
      syncWhenAlpineReady();
    } else {
      document.addEventListener('alpine:init', syncWhenAlpineReady, { once: true });
    }
  }

  window.addEventListener('pageshow', handlePageShow);

  document.addEventListener('alpine:init', () => {
    ensureCartPatches();
  });

  function boot() {
    observeCartDrawer();
    waitForCartPatches();
    watchMiniCartLoading();
    schedulePageWishlistResync();
  }

  window.__almapharmWishlist = {
    resync: schedulePageWishlistResync,
    syncCartButtons: afterCartDomUpdateSoon,
  };

  getWishlistKing().catch(() => {});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}
